import React from "react";

interface ProductImage {
  url: string;
  caption?: string;
}

interface Product {
  id: number;
  name: string;
  shortDescription?: string;
  images?: ProductImage[];
}

interface WholesaleCampaignFormProps {
  // later: props for saving, initial values, etc.
}

const WholesaleCampaignForm: React.FC<WholesaleCampaignFormProps> = () => {
  // Text fields
  const [subject, setSubject] = React.useState("");
  const [preheader, setPreheader] = React.useState("");
  const [intro, setIntro] = React.useState("");
  const [featureTitle, setFeatureTitle] = React.useState("");
  const [orderCtaText, setOrderCtaText] = React.useState(
    "Reply to this email or contact us to place your order."
  );
  const [orderContactName, setOrderContactName] = React.useState(
    "Ridgetop Botanicals Wholesale"
  );
  const [orderContactEmail, setOrderContactEmail] = React.useState(
    "wholesale@example.com"
  );
  const [orderContactPhone, setOrderContactPhone] = React.useState("");
  const [footerNotes, setFooterNotes] = React.useState(
    "This email is intended only for licensed cannabis businesses. Please do not forward publicly."
  );

  // Products
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedProductIds, setSelectedProductIds] = React.useState<number[]>(
    []
  );
  const [isLoadingProducts, setIsLoadingProducts] = React.useState(false);
  const [productError, setProductError] = React.useState<string | null>(null);

React.useEffect(() => {
  const loadProducts = async () => {
    setIsLoadingProducts(true);
    setProductError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/products`
      );
      if (!res.ok) {
        throw new Error(`Failed to load products: ${res.status}`);
      }

      const raw = await res.json();
      console.log("Products API response:", raw);

      let list: Product[] = [];

      if (Array.isArray(raw)) {
        // backend returns plain array: [ { id, name, ... }, ... ]
        list = raw as Product[];
      } else if (Array.isArray((raw as any).products)) {
        // backend returns { products: [...] }
        list = (raw as any).products as Product[];
      } else if (Array.isArray((raw as any).items)) {
        // backend returns { items: [...] }
        list = (raw as any).items as Product[];
      } else {
        console.error("Unexpected products API shape:", raw);
        throw new Error("Unexpected products API shape");
      }

      setProducts(list);
    } catch (err: any) {
      console.error("Error loading products:", err);
      setProductError("Failed to load products.");
      setProducts([]); // make sure it's an array
    } finally {
      setIsLoadingProducts(false);
    }
  };

  loadProducts();
}, []);


  const toggleProduct = (id: number) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Build the payload we’ll eventually POST to your backend
  const buildCampaignPayload = () => {
    return {
      type: "WHOLESALE_PRODUCT_DROP",
      listIds: [4], // Distributors – Wholesale (ID 4)
      subject,
      preheader,
      intro,
      featureTitle,
      orderCtaText,
      orderContact: {
        name: orderContactName,
        email: orderContactEmail,
        phone: orderContactPhone || null,
      },
      footerNotes,
      productIds: selectedProductIds,
    };
  };

const handleSaveDraft = async () => {
  const payload = buildCampaignPayload();
  console.log("Wholesale campaign draft payload (frontend):", payload);

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/campaigns/wholesale`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      console.error("Failed to save campaign draft:", res.status);
      alert("Failed to save campaign draft (check backend logs).");
      return;
    }

    const data = await res.json();
    console.log("Backend response:", data);
    alert("Campaign payload successfully sent to backend.");
  } catch (err) {
    console.error("Error saving campaign draft:", err);
    alert("Error saving campaign draft (check console).");
  }
};


  const selectedProducts = products.filter((p) =>
    selectedProductIds.includes(p.id)
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
      {/* Left: form fields */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold">Wholesale Product Drop Details</h4>

        {/* Product selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Select Products
          </label>
          <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
            {isLoadingProducts && (
              <p className="text-xs text-gray-500">Loading products…</p>
            )}
            {productError && (
              <p className="text-xs text-red-500">{productError}</p>
            )}
            {!isLoadingProducts && !productError && products.length === 0 && (
              <p className="text-xs text-gray-500">No products found.</p>
            )}
            {products.map((product) => (
              <label
                key={product.id}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedProductIds.includes(product.id)}
                  onChange={() => toggleProduct(product.id)}
                />
                {product.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Subject</label>
          <input
            type="text"
            className="w-full border rounded px-2 py-1"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="New Fall Harvest Drop – Batch XYZ Now Available"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Preheader</label>
          <input
            type="text"
            className="w-full border rounded px-2 py-1"
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Fresh batches with full COAs, available for order."
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Intro</label>
          <textarea
            className="w-full border rounded px-2 py-1 h-24"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="Short context for this drop (season, farm update, what’s special about these batches)."
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Feature title (optional)
          </label>
          <input
            type="text"
            className="w-full border rounded px-2 py-1"
            value={featureTitle}
            onChange={(e) => setFeatureTitle(e.target.value)}
            placeholder="Late Fall Harvest Drop"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Ordering CTA text
          </label>
          <textarea
            className="w-full border rounded px-2 py-1 h-20"
            value={orderCtaText}
            onChange={(e) => setOrderCtaText(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              Wholesale contact name
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1"
              value={orderContactName}
              onChange={(e) => setOrderContactName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Wholesale contact email
            </label>
            <input
              type="email"
              className="w-full border rounded px-2 py-1"
              value={orderContactEmail}
              onChange={(e) => setOrderContactEmail(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Wholesale contact phone (optional)
          </label>
          <input
            type="text"
            className="w-full border rounded px-2 py-1"
            value={orderContactPhone}
            onChange={(e) => setOrderContactPhone(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Footer notes / disclaimer
          </label>
          <textarea
            className="w-full border rounded px-2 py-1 h-20 text-xs"
            value={footerNotes}
            onChange={(e) => setFooterNotes(e.target.value)}
          />
        </div>

        <button
          type="button"
          onClick={handleSaveDraft}
          className="mt-2 w-full bg-green-600 text-white rounded px-3 py-2 text-sm font-medium hover:bg-green-700"
        >
          Save Draft (log payload)
        </button>
      </div>

      {/* Right: preview */}
      <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
        <h4 className="text-lg font-semibold">Email Preview (simplified)</h4>

        <div className="text-sm">
          <div>
            <span className="font-semibold">Subject:</span>{" "}
            {subject || "(no subject yet)"}
          </div>
          <div className="text-gray-500">
            <span className="font-semibold">Preheader:</span>{" "}
            {preheader || "(no preheader yet)"}
          </div>
        </div>

        <hr />

        <div className="text-sm space-y-2">
          <p>Hi {"{{ buyer_name }}"},</p>

          <p>{intro || "(intro will go here…)"}</p>

          {featureTitle && (
            <p className="font-semibold mt-2">{featureTitle}</p>
          )}

          <div className="mt-4 space-y-3">
            {selectedProducts.length === 0 && (
              <p className="text-gray-500 text-sm">No products selected yet.</p>
            )}

            {selectedProducts.map((p) => (
              <div key={p.id} className="border rounded p-2 bg-white">
                <strong>{p.name}</strong>
                {p.shortDescription && (
                  <p className="text-xs text-gray-600">
                    {p.shortDescription}
                  </p>
                )}

                {p.images && p.images.length > 0 && (
                  <img
                    src={p.images[0].url}
                    alt={p.images[0].caption || p.name}
                    className="mt-2 w-32 rounded"
                  />
                )}

                <p className="text-xs mt-1 italic text-gray-500">
                  COA details will be inserted when sending.
                </p>
              </div>
            ))}
          </div>

          <p className="mt-4">{orderCtaText}</p>

          <div className="mt-4 text-xs text-gray-700">
            <div>{orderContactName}</div>
            <div>{orderContactEmail}</div>
            {orderContactPhone && <div>{orderContactPhone}</div>}
          </div>

          <p className="mt-4 text-[11px] text-gray-500">{footerNotes}</p>
        </div>
      </div>
    </div>
  );
};

export default WholesaleCampaignForm;
